// A machine's ONE jobs state (#6746, epic #6552): the worst of the jobs it holds for the
// viewing account, decided here and carried on the per-machine verdict (`machineVerdict`,
// `./machine-verdict.ts`) by both doors that publish it: `GET /v1/fleet/utilization`
// `boxes[].verdict.jobs_state` and MCP `box fleet_status` `boxes[].verdict.jobs_state`.
// Pure: the doors read the held rows (`fleetBoxJobs`, `@developerz/db`) and the beat, and
// hand both to {@link machineJobInputs}; nothing here reads a clock, the KV or a database.
//
// NOT THE CARD'S PER-JOB STATE. `MachineJobState` (`../../wire/machine-job.ts`, #6745) is
// what one job row on the card shows; this union is the machine's fold and adds the two
// states no job row carries (`stuck`, `stopping`). The beat half of the two is ONE rule,
// {@link heldJobBeatState}, which the card's merge (`boxJobsView`, `apps/api`) and this
// fold both call, so a job cannot read `working` on the card and `starting` in the fold.
//
// The stall line restates a constant this package may not import (domain imports nothing
// internal): {@link MACHINE_JOB_STUCK_AFTER_MS} = `ADAPTER_CEILING_MINUTES` (`apps/worker`
// `cron/stuck-task.ts`), pinned by `apps/worker/src/cron/machine-verdict-thresholds.test.ts`.

import type { MachineJobStatus } from '../../wire/machine-job';

/**
 * Silent this long, a held job reads `stuck`: the platform's "something is quiet" line, the
 * fleet warden's yellow tier and the `stuck_observed` alert to #fleet-alerts (both set to
 * `ADAPTER_CEILING_MINUTES`, 60). The reaper re-pends the task at 180 minutes
 * (`STUCK_TASK_SILENCE_MINUTES`), so a job reads stuck for two hours before it is put back
 * in line; a window at 180 would show stuck only as the job disappears.
 */
export const MACHINE_JOB_STUCK_AFTER_MS = 60 * 60 * 1000;

/** Worst first: the v2 PRD's order, and the order of {@link MACHINE_JOBS_VERDICT_RANK}. */
export const MACHINE_JOBS_VERDICT_STATES = [
  'stuck',
  'stopping',
  'pausing',
  'starting',
  'working',
  'paused',
] as const;
export type MachineJobsVerdictState = (typeof MACHINE_JOBS_VERDICT_STATES)[number];

/**
 * The fold's total order, lower is worse: stuck > stopping > pausing > starting > working >
 * paused. A total `Record`, so a state added to the union fails to compile until it is
 * ranked here. `pausing` (a stop the person asked for) sits between `stopping` (a stop the
 * platform asked for) and `starting`; `paused` is last, because a paused job runs nothing
 * and is silent by design.
 */
export const MACHINE_JOBS_VERDICT_RANK: Readonly<Record<MachineJobsVerdictState, number>> = {
  stuck: 0,
  stopping: 1,
  pausing: 2,
  starting: 3,
  working: 4,
  paused: 5,
};

/** What the machine's live beat says of one held row. */
export type MachineJobBeatState = 'working' | 'starting';

/**
 * THE per-box beat rule (owner ruling, #6552 issuecomment-6553761308, item 2), over
 * primitives only. `beatTaskIds` is the beat's `jobs` key read as task ids: an array (`[]`
 * is a reading, "no live job") means the machine reports its jobs, so a row it names is
 * `working` and one it does not is `starting`; `null` (no `jobs` key: a runner older than
 * #6743, or a beat whose sampler failed) means it does not, so the row reads by its stored
 * status, `in_progress` as `working` and anything else as `starting`.
 */
export function heldJobBeatState(
  status: MachineJobStatus,
  taskId: string,
  beatTaskIds: readonly string[] | null,
): MachineJobBeatState {
  if (beatTaskIds === null) return status === 'in_progress' ? 'working' : 'starting';
  return beatTaskIds.includes(taskId) ? 'working' : 'starting';
}

/** One held job as the fold reads it. */
export type MachineJobInput = {
  /** {@link heldJobBeatState}'s answer for the row. */
  beat: MachineJobBeatState;
  /** A cancel is requested on the box-held row (`cancelRequestedSql`, `@developerz/db`). */
  cancelRequested: boolean;
  /** A started (`in_progress`) row with `pause_requested_at` set (`pauseRequestedSql`). */
  pauseRequested: boolean;
  /** The row is `paused` (#6747): no process runs. */
  paused: boolean;
  /** A `pending` row pinned to this machine after a resume (#6748): no run yet. */
  pinnedWaiting: boolean;
  /** {@link machineJobSilentSinceMs}: when the job last showed it was moving. */
  silentSinceMs: number;
};

/**
 * When a job last showed it was moving: the LATER of its last status change
 * (`tasks.updated_at`) and its newest progress event (`task_run_events.ts`, `run_heartbeat`
 * excluded by `isProgressEvent`, `@developerz/db`). The two proofs the stuck-task reaper
 * selects on (`listStuckTasks`): a heartbeat never hides a hung run, and an old run's events
 * never make a fresh claim look silent.
 */
export function machineJobSilentSinceMs(updatedAtMs: number, lastProgressAtMs: number | null) {
  return lastProgressAtMs === null ? updatedAtMs : Math.max(updatedAtMs, lastProgressAtMs);
}

/**
 * One job's state, first match wins:
 *  1. `paused`: no process runs, so silence is expected; it never reads stuck.
 *  2. `starting` for a pinned waiting row: no run yet, so silence is expected here too.
 *  3. `stuck`: silent for at least {@link MACHINE_JOB_STUCK_AFTER_MS}. A cancel or pause
 *     request does not hide a hung run.
 *  4. `stopping`: a cancel is requested and the box has not ended the run.
 *  5. `pausing`: a pause is requested and the box has not stopped.
 *  6. the beat: `starting` or `working` ({@link heldJobBeatState}).
 */
export function machineJobState(job: MachineJobInput, nowMs: number): MachineJobsVerdictState {
  if (job.paused) return 'paused';
  if (job.pinnedWaiting) return 'starting';
  if (nowMs - job.silentSinceMs >= MACHINE_JOB_STUCK_AFTER_MS) return 'stuck';
  if (job.cancelRequested) return 'stopping';
  if (job.pauseRequested) return 'pausing';
  return job.beat;
}

/** The worst job's state by {@link MACHINE_JOBS_VERDICT_RANK}; `null` when no job counts. */
export function machineJobsVerdict(
  jobs: readonly MachineJobInput[],
  nowMs: number,
): MachineJobsVerdictState | null {
  let worst: MachineJobsVerdictState | null = null;
  for (const job of jobs) {
    const state = machineJobState(job, nowMs);
    if (worst === null || MACHINE_JOBS_VERDICT_RANK[state] < MACHINE_JOBS_VERDICT_RANK[worst]) {
      worst = state;
    }
  }
  return worst;
}

/**
 * One held row's stored facts, as the ONE jobs read (`fleetBoxJobs`, `@developerz/db`)
 * returns them; its `FleetBoxJob` is this shape plus the card's fields. Both doors pass
 * those rows here unchanged: the REST row through `fleetBoxes`, MCP through the `heldJobs`
 * port.
 */
export type MachineHeldJob = {
  readonly id: string;
  readonly status: MachineJobStatus;
  /** `cancelRequestedSql()` on the row. */
  readonly cancelRequested: boolean;
  /** `pauseRequestedSql()` on the row, any status. */
  readonly pauseRequested: boolean;
  /** `tasks.updated_at`: the last status change. */
  readonly updatedAt: Date;
  /** The newest progress event's `ts` (`isProgressEvent`), or null when the run has none. */
  readonly lastProgressAt: Date | null;
};

/**
 * The machine's liveness as the fold reads it. `absent` is no live check-in: no presence
 * key, a key that does not read `online`, an undecodable key, or a presence read that
 * could not be made. Each door decides it the way #6745's `jobPresence` does; `live`
 * carries the beat's task ids, `null` when the beat has no `jobs` key.
 */
export type MachineJobsBeat =
  | { readonly kind: 'absent' }
  | { readonly kind: 'live'; readonly taskIds: readonly string[] | null };

/**
 * The fold's input from the stored rows and the machine's beat. A machine with no live
 * check-in contributes NO job, paused rows included: its own status (`not_responding`,
 * `offline`) already says it, so its jobs are last known and leave the jobs state null. The
 * filter is on the MACHINE's liveness, never on a job's state.
 */
export function machineJobInputs(
  jobs: readonly MachineHeldJob[],
  beat: MachineJobsBeat,
): MachineJobInput[] {
  if (beat.kind === 'absent') return [];
  return jobs.map((job) => ({
    beat: heldJobBeatState(job.status, job.id, beat.taskIds),
    cancelRequested: job.cancelRequested,
    // Only a STARTED run pauses: the card reads an `assigned` row with the stamp by its beat.
    pauseRequested: job.status === 'in_progress' && job.pauseRequested,
    paused: job.status === 'paused',
    pinnedWaiting: job.status === 'pending',
    silentSinceMs: machineJobSilentSinceMs(
      job.updatedAt.getTime(),
      job.lastProgressAt?.getTime() ?? null,
    ),
  }));
}
